import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "utils/redux/store";
import { responseStatus } from "utils/types";
import { REACT_APP_API_URL } from "utils/environmentVariables";
import { request } from "utils/request";
import {
  DepartmentCardInfo,
  DepartmentEvent,
  DepartmentTrackingEvent,
  PendingTeamLeader,
  AllDepartmentAssessmentInfo,
  DepartmentResultsInfo,
  DepartmentResultsInfoByDepartmentId,
  PendingDepartmentLeader,
  OverallActivitySnapshotByDateInterval,
  TEAMscanActivitySnapshotByDateInterval,
  TEAMscanActivitySnapshot,
  DashboardSubpageShowing,
  PeopleWithDeliverabilityIssue,
  AllEmailDeliverabilityInformation,
} from "./types";
import { toast } from "react-toastify";
import { successNotificationOptions } from "utils/constants";

// ------------------ State Type/Structure ------------------
export interface AdminConsoleState {
  departmentCardInfoById: {
    [departmentId: number]: DepartmentCardInfo;
  };
  departmentTrackingEvents: {
    [departmentId: number]: DepartmentTrackingEvent[];
  };
  getDepartmentTrackingEventsStatus: responseStatus;
  teamHasOpenAssessmentById: {
    [teamId: number]: boolean;
  };
  departmentTeamLeaders: {
    [departmentId: number]: PendingTeamLeader[];
  };
  allEmailDeliverabilityInformation: {
    [timeInterval: string]: AllEmailDeliverabilityInformation;
  };
  peopleWithDeliverabilityIssues: PeopleWithDeliverabilityIssue[];
  hasAnyWeeklyCheckinResults: boolean;
  allPendingTeamLeaders: PendingTeamLeader[];
  allPendingDepartmentLeaders: PendingDepartmentLeader[];
  departmentAssessmentInfo: AllDepartmentAssessmentInfo;
  departmentResultsInfoByDepartmentId: DepartmentResultsInfoByDepartmentId;
  overallActivitySnapshotByDateInterval: OverallActivitySnapshotByDateInterval;
  teamScanActivitySnapshotByDateInterval: TEAMscanActivitySnapshotByDateInterval;
  subpageShowing: DashboardSubpageShowing | null;
  getTEAMscanActivitySnapshotStatus: responseStatus;
  getOverallActivitySnapshotStatus: responseStatus;
  getEmailDeliverabilityInformationStatus: responseStatus;
  getPeopleWithDeliverabilityIssuesStatus: responseStatus;
  getAdminTeamsTeam360AnalyticsTextStatus: responseStatus;
  getDepartmentAssessmentInfoStatus: responseStatus;
  getDepartmentPendingTeamLeadersStatus: responseStatus;
  getDepartmentCardInfoStatus: responseStatus;
  getTeamHasOpenAssessmentStatus: responseStatus;
  getDepartmentResultsInfoStatus: responseStatus;
  remindTeamLeadersStatus: responseStatus;
  allPendingLeadersHaveBeenReminded: boolean;
  checkIfAllPendingLeadersHaveBeenRemindedStatus: responseStatus;
  gettingAllPendingTeamLeadersStatus: responseStatus;
  gettingAllPendingDepartmentLeadersStatus: responseStatus;
}

// ------------------ InitialState ------------------
const initialState: AdminConsoleState = {
  departmentTrackingEvents: {},
  departmentCardInfoById: {},
  departmentTeamLeaders: {},
  teamHasOpenAssessmentById: {},
  departmentAssessmentInfo: {},
  departmentResultsInfoByDepartmentId: {},
  allPendingLeadersHaveBeenReminded: false,
  allPendingTeamLeaders: [],
  allPendingDepartmentLeaders: [],
  overallActivitySnapshotByDateInterval: {},
  teamScanActivitySnapshotByDateInterval: {},
  hasAnyWeeklyCheckinResults: false,
  subpageShowing: null,
  allEmailDeliverabilityInformation: {},
  peopleWithDeliverabilityIssues: [],
  getEmailDeliverabilityInformationStatus: "idle",
  getPeopleWithDeliverabilityIssuesStatus: "idle",
  getTEAMscanActivitySnapshotStatus: "idle",
  getOverallActivitySnapshotStatus: "idle",
  getAdminTeamsTeam360AnalyticsTextStatus: "idle",
  getDepartmentAssessmentInfoStatus: "idle",
  getDepartmentPendingTeamLeadersStatus: "idle",
  getDepartmentTrackingEventsStatus: "idle",
  getDepartmentCardInfoStatus: "idle",
  getTeamHasOpenAssessmentStatus: "idle",
  getDepartmentResultsInfoStatus: "idle",
  remindTeamLeadersStatus: "idle",
  checkIfAllPendingLeadersHaveBeenRemindedStatus: "idle",
  gettingAllPendingTeamLeadersStatus: "idle",
  gettingAllPendingDepartmentLeadersStatus: "idle",
};

// ------------------------------------ GETS ------------------------------------

export const getDepartmentTrackingEvents = createAsyncThunk(
  "dashboard/getDepartmentTrackingEvents",
  async (departmentId: number) => {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/departments/${departmentId}/events`;
    const response = (await request(requestUrl)) as DepartmentTrackingEvent[];
    return response;
  }
);

export const getDepartmentCardInfo = createAsyncThunk(
  "dashboard/getDepartmentCardInfo",
  async (departmentId: number) => {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/departments/${departmentId}/cardInfo`;
    const response = (await request(requestUrl)) as DepartmentCardInfo;
    return {
      response,
      departmentId,
    };
  }
);

export const getDepartmentPendingTeamLeaders = createAsyncThunk(
  "dashboard/getDepartmentPendingTeamLeaders",
  async (departmentId: number) => {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/departments/${departmentId}/pendingTeamLeaders`;
    const response = (await request(requestUrl)) as PendingTeamLeader[];
    return { response, departmentId };
  }
);

export const getDepartmentAssessmentCompletionInfo = createAsyncThunk(
  "dashboard/getAssessmentCompletionInfo",
  async () => {
    // TODO: Might change this endpoint to return the assessment information for a specific department instead of all departments
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/getAssessmentCompletionInfo`;
    const response = (await request(requestUrl)) as {
      departments: AllDepartmentAssessmentInfo;
    };
    return response?.departments;
  }
);

export const getDepartmentResultsInfo = createAsyncThunk(
  "dashboard/getDepartmentResultsInfo",
  async (departmentId: number) => {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/departments/${departmentId}/TEAM360ResultsInfo`;
    const response = (await request(requestUrl)) as DepartmentResultsInfo;
    return { response, departmentId };
  }
);

export const remindTeamLeaders = createAsyncThunk(
  "dashboard/remindTeamLeaders",
  async ({ departmentId }: { departmentId: number }) => {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/departments/${departmentId}/sendReminderToTeamLeads`;
    const response = await request(requestUrl, {
      method: "GET",
    });
    return response;
  }
);

export const checkIfAllPendingLeadersHaveBeenReminded = createAsyncThunk(
  "dashboard/checkIfAllPendingLeadersHaveBeenReminded",
  async ({ departmentId }: { departmentId: number }) => {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/departments/${departmentId}/allPendingLeadersHaveBeenReminded`;
    const response = (await request(requestUrl, {
      method: "GET",
    })) as { allPendingLeadersHaveBeenReminded: boolean };
    return response;
  }
);

export const getTeamHasOpenAssessment = createAsyncThunk(
  "teamGuide/getTeamHasOpenAssessment",
  async (teamId: number) => {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/teams/${teamId}/openAssessment`;
    const getTeamHasOpenAssessmentResponse = (await request(requestUrl)) as {
      teamHasOpenAssessment: boolean;
    };

    return { response: getTeamHasOpenAssessmentResponse, teamId };
  }
);

export const getPendingAllPendingTeamLeaders = createAsyncThunk(
  "dashboard/getPendingAllPendingTeamLeaders",
  async () => {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/pendingTeamLeaders`;
    const response = (await request(requestUrl)) as PendingTeamLeader[];
    return response;
  },
  {
    condition: (_, { getState }) => {
      const {
        dashboard: { gettingAllPendingTeamLeadersStatus },
      } = getState() as RootState;
      if (gettingAllPendingTeamLeadersStatus !== "idle") {
        return false;
      }
    },
  }
);

export const getPendingDepartmentLeaders = createAsyncThunk(
  "dashboard/getPendingDepartmentLeaders",
  async () => {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/pendingDepartmentLeaders`;
    const response = (await request(requestUrl)) as PendingDepartmentLeader[];
    return response;
  },
  {
    condition: (_, { getState }) => {
      const {
        dashboard: { gettingAllPendingDepartmentLeadersStatus },
      } = getState() as RootState;
      if (gettingAllPendingDepartmentLeadersStatus !== "idle") {
        return false;
      }
    },
  }
);

export const getOverallActivitySnapshot = createAsyncThunk(
  "dashboard/getOverallActivitySnapshot",
  async () => {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/overallActivitySnapshot`;
    const response = (await request(
      requestUrl
    )) as OverallActivitySnapshotByDateInterval;
    const hasAnyWeeklyCheckinResults = Object.values(response).some(
      (overallActivitySnapshot) => {
        return !!overallActivitySnapshot?.weeklyCheckInCount?.totalCount;
      }
    );
    return { response, hasAnyWeeklyCheckinResults };
  },
  {
    condition: (_, { getState }) => {
      const {
        dashboard: { getOverallActivitySnapshotStatus },
      } = getState() as RootState;
      if (getOverallActivitySnapshotStatus !== "idle") {
        return false;
      }
    },
  }
);

export const getEmailDeliverabilityInformation = createAsyncThunk(
  "dashboard/getEmailDeliverabilityInformation",
  async ({
    timeInterval,
  }: {
    timeInterval: string;
    forceRefresh?: boolean;
  }) => {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/emailAnalytics/${timeInterval}`;
    const response = (await request(
      requestUrl
    )) as AllEmailDeliverabilityInformation;
    return { response, timeInterval };
  },
  {
    condition: ({ forceRefresh, timeInterval }, { getState }) => {
      const {
        dashboard: {
          getEmailDeliverabilityInformationStatus,
          allEmailDeliverabilityInformation,
        },
      } = getState() as RootState;

      if (allEmailDeliverabilityInformation[timeInterval] && !forceRefresh) {
        return false;
      }

      if (getEmailDeliverabilityInformationStatus === "loading") {
        return false;
      }
    },
  }
);

export const getPeopleWithDeliverabilityIssues = createAsyncThunk(
  "dashboard/getPeopleWithDeliverabilityIssues",
  async () => {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/peopleWithEmailDeliverabilityIssues`;
    const response = (await request(
      requestUrl
    )) as PeopleWithDeliverabilityIssue[];

    return response;
  },
  {
    condition: (forceRefresh: boolean | undefined, { getState }) => {
      const {
        dashboard: { getPeopleWithDeliverabilityIssuesStatus },
      } = getState() as RootState;
      if (getPeopleWithDeliverabilityIssuesStatus !== "idle" && !forceRefresh) {
        return false;
      }
    },
  }
);

// ------------------------------------ PUTS ------------------------------------

// ------------------------------------ POSTS ------------------------------------

export const trackDepartmentEvent = createAsyncThunk(
  "dashboard/trackDepartmentEvent",
  async ({
    departmentId,
    event,
  }: {
    departmentId: number;
    event: DepartmentEvent;
  }) => {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/departments/${departmentId}/trackEvent`;
    const response = await request(requestUrl, {
      method: "POST",
      body: JSON.stringify({ event }),
    });
    return response;
  }
);

export const sendReminder = createAsyncThunk(
  "dashboard/sendReminder",
  async (payload: {
    reminderType:
      | "remindTeamLeader"
      | "remindTeamInvitation"
      | "remindUserToTakeAssessment"
      | "invitationReminder";
    userAccountId?: number;
    teamId?: number;
  }) => {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/sendReminder`;
    const response = await request(requestUrl, {
      method: "POST",
      body: JSON.stringify(payload),
    });
    return response;
  }
);

export const getTEAMscanActivitySnapshot = createAsyncThunk(
  "dashboard/getTEAMscanActivitySnapshot",
  async (timeFrame: "past90Days" | "past365Days") => {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/TEAMscanActivityDashboard`;
    const response = (await request(requestUrl, {
      method: "POST",
      body: JSON.stringify({ timeFrame }),
    })) as TEAMscanActivitySnapshot;

    return { response, timeFrame };
  },
  {
    condition: (timeFrame, { getState }) => {
      const {
        dashboard: { teamScanActivitySnapshotByDateInterval },
      } = getState() as RootState;

      if (teamScanActivitySnapshotByDateInterval[timeFrame]) {
        return false;
      }
    },
  }
);

// ------------------------------------ DELETES ------------------------------------

// ------------------ Beginning of Slice ------------------
export const dashboardSlice = createSlice({
  name: "dashboard",
  initialState,
  reducers: {
    addInvitedLeader: (
      state,
      { payload }: PayloadAction<PendingTeamLeader>
    ) => {
      return {
        ...state,
        departmentTeamLeaders: {
          ...state.departmentTeamLeaders,
          [payload.departmentId]: [
            ...(state.departmentTeamLeaders[payload.departmentId] || []),
            payload,
          ],
        },
      };
    },
    remindTeamLeaderForDepartment: (
      state,
      {
        payload,
      }: PayloadAction<{
        userAccountId: number;
        departmentId: number;
      }>
    ) => {
      const previousDepartmentTeamLeaders =
        state.departmentTeamLeaders[payload.departmentId];

      const updatedDepartmentTeamLeaders =
        previousDepartmentTeamLeaders?.map((teamLeader) =>
          teamLeader.userAccountId === payload.userAccountId
            ? { ...teamLeader, isReminded: true }
            : teamLeader
        ) ?? [];
      const updatedAllPendingTeamLeaders = state.allPendingTeamLeaders.map(
        (teamLeader) =>
          teamLeader.userAccountId === payload.userAccountId &&
          teamLeader.ti_onboardingDepartmentId === payload.departmentId
            ? { ...teamLeader, isReminded: true }
            : teamLeader
      );

      return {
        ...state,
        departmentTeamLeaders: {
          ...state.departmentTeamLeaders,
          [payload.departmentId]: updatedDepartmentTeamLeaders,
        },
        allPendingTeamLeaders: updatedAllPendingTeamLeaders,
      };
    },
    setSubpageShowing: (
      state,
      action: PayloadAction<DashboardSubpageShowing | null>
    ) => {
      state.subpageShowing = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getDepartmentCardInfo.pending, (state) => {
        state.getDepartmentCardInfoStatus = "loading";
      })
      .addCase(
        getDepartmentCardInfo.fulfilled,
        (
          state,
          {
            payload: { response, departmentId },
          }: PayloadAction<{
            response: DepartmentCardInfo;
            departmentId: number;
          }>
        ) => {
          return {
            ...state,
            getDepartmentCardInfoStatus: "succeeded",
            departmentCardInfoById: {
              ...state.departmentCardInfoById,
              [departmentId]: {
                ...response,
                departmentId,
              },
            },
          };
        }
      )
      .addCase(getDepartmentCardInfo.rejected, (state) => {
        state.getDepartmentCardInfoStatus = "failed";
      })
      .addCase(getDepartmentTrackingEvents.rejected, (state) => {
        state.getDepartmentTrackingEventsStatus = "failed";
      })
      .addCase(getDepartmentTrackingEvents.pending, (state) => {
        state.getDepartmentTrackingEventsStatus = "loading";
      })
      .addCase(
        getDepartmentTrackingEvents.fulfilled,
        (state, { payload }: PayloadAction<DepartmentTrackingEvent[]>) => {
          const departmentTrackingEvents: {
            [departmentId: number]: DepartmentTrackingEvent[];
          } = {};
          payload.forEach((departmentTrackingEvent) => {
            const { departmentId } = departmentTrackingEvent;
            departmentTrackingEvents[departmentId] =
              departmentTrackingEvents[departmentId] || [];
            departmentTrackingEvents[departmentId].push(
              departmentTrackingEvent
            );
          });

          return {
            ...state,
            getDepartmentTrackingEventsStatus: "succeeded",
            departmentTrackingEvents: {
              ...state.departmentTrackingEvents,
              ...departmentTrackingEvents,
            },
          };
        }
      )
      .addCase(getTeamHasOpenAssessment.pending, (state) => {
        state.getTeamHasOpenAssessmentStatus = "loading";
      })
      .addCase(
        getTeamHasOpenAssessment.fulfilled,
        (
          state,
          {
            payload: { response, teamId },
          }: PayloadAction<{
            response: { teamHasOpenAssessment: boolean };
            teamId: number;
          }>
        ) => {
          return {
            ...state,
            getTeamHasOpenAssessmentStatus: "succeeded",
            teamHasOpenAssessmentById: {
              ...state.teamHasOpenAssessmentById,
              [teamId]: response.teamHasOpenAssessment,
            },
          };
        }
      )
      .addCase(getTeamHasOpenAssessment.rejected, (state) => {
        state.getTeamHasOpenAssessmentStatus = "failed";
      })
      .addCase(getDepartmentPendingTeamLeaders.pending, (state) => {
        state.getDepartmentPendingTeamLeadersStatus = "loading";
      })
      .addCase(
        getDepartmentPendingTeamLeaders.fulfilled,
        (
          state,
          {
            payload: { response, departmentId },
          }: PayloadAction<{
            response: PendingTeamLeader[];
            departmentId: number;
          }>
        ) => {
          return {
            ...state,
            getDepartmentPendingTeamLeadersStatus: "succeeded",
            departmentTeamLeaders: {
              ...state.departmentTeamLeaders,
              [departmentId]: response,
            },
          };
        }
      )
      .addCase(getDepartmentPendingTeamLeaders.rejected, (state) => {
        state.getDepartmentPendingTeamLeadersStatus = "failed";
      })
      .addCase(getDepartmentAssessmentCompletionInfo.pending, (state) => {
        state.getDepartmentAssessmentInfoStatus = "loading";
      })
      .addCase(
        getDepartmentAssessmentCompletionInfo.fulfilled,
        (state, { payload }: PayloadAction<AllDepartmentAssessmentInfo>) => {
          return {
            ...state,
            getDepartmentAssessmentInfoStatus: "succeeded",
            departmentAssessmentInfo: payload,
          };
        }
      )
      .addCase(getDepartmentAssessmentCompletionInfo.rejected, (state) => {
        state.getDepartmentAssessmentInfoStatus = "failed";
      })
      .addCase(getDepartmentResultsInfo.pending, (state) => {
        state.getDepartmentResultsInfoStatus = "loading";
      })
      .addCase(getDepartmentResultsInfo.fulfilled, (state, { payload }) => {
        return {
          ...state,
          getDepartmentResultsInfoStatus: "succeeded",
          departmentResultsInfoByDepartmentId: {
            ...state.departmentResultsInfoByDepartmentId,
            [payload.departmentId]: payload.response,
          },
        };
      })
      .addCase(getDepartmentResultsInfo.rejected, (state) => {
        state.getDepartmentResultsInfoStatus = "failed";
      })
      .addCase(checkIfAllPendingLeadersHaveBeenReminded.pending, (state) => {
        state.checkIfAllPendingLeadersHaveBeenRemindedStatus = "loading";
      })
      .addCase(
        checkIfAllPendingLeadersHaveBeenReminded.fulfilled,
        (state, { payload }) => {
          state.checkIfAllPendingLeadersHaveBeenRemindedStatus = "succeeded";
          state.allPendingLeadersHaveBeenReminded =
            payload.allPendingLeadersHaveBeenReminded;
        }
      )
      .addCase(checkIfAllPendingLeadersHaveBeenReminded.rejected, (state) => {
        state.checkIfAllPendingLeadersHaveBeenRemindedStatus = "failed";
      })
      .addCase(remindTeamLeaders.pending, (state) => {
        state.remindTeamLeadersStatus = "loading";
      })
      .addCase(remindTeamLeaders.fulfilled, (state) => {
        state.remindTeamLeadersStatus = "succeeded";
        toast.success("Reminders sent", successNotificationOptions);
      })
      .addCase(remindTeamLeaders.rejected, (state) => {
        state.remindTeamLeadersStatus = "failed";
      })
      .addCase(sendReminder.fulfilled, () => {
        toast.success("Reminders sent", successNotificationOptions);
      })
      .addCase(getPendingAllPendingTeamLeaders.pending, (state) => {
        state.gettingAllPendingTeamLeadersStatus = "loading";
      })
      .addCase(
        getPendingAllPendingTeamLeaders.fulfilled,
        (state, { payload }: PayloadAction<PendingTeamLeader[]>) => {
          state.allPendingTeamLeaders = payload ?? state.allPendingTeamLeaders;
          state.gettingAllPendingTeamLeadersStatus = "succeeded";
        }
      )
      .addCase(getPendingAllPendingTeamLeaders.rejected, (state) => {
        state.gettingAllPendingTeamLeadersStatus = "failed";
      })
      .addCase(getPendingDepartmentLeaders.pending, (state) => {
        state.gettingAllPendingDepartmentLeadersStatus = "loading";
      })
      .addCase(
        getPendingDepartmentLeaders.fulfilled,
        (state, { payload }: PayloadAction<PendingDepartmentLeader[]>) => {
          state.allPendingDepartmentLeaders =
            payload ?? state.allPendingDepartmentLeaders;
          state.gettingAllPendingDepartmentLeadersStatus = "succeeded";
        }
      )
      .addCase(getPendingDepartmentLeaders.rejected, (state) => {
        state.gettingAllPendingDepartmentLeadersStatus = "failed";
      })
      .addCase(getOverallActivitySnapshot.pending, (state) => {
        state.getOverallActivitySnapshotStatus = "loading";
      })
      .addCase(
        getOverallActivitySnapshot.fulfilled,
        (
          state,
          {
            payload: { response, hasAnyWeeklyCheckinResults },
          }: PayloadAction<{
            response: OverallActivitySnapshotByDateInterval;
            hasAnyWeeklyCheckinResults: boolean;
          }>
        ) => {
          state.getOverallActivitySnapshotStatus = "succeeded";
          state.overallActivitySnapshotByDateInterval = response;
          state.hasAnyWeeklyCheckinResults = hasAnyWeeklyCheckinResults;
        }
      )
      .addCase(getOverallActivitySnapshot.rejected, (state) => {
        state.getOverallActivitySnapshotStatus = "failed";
      })
      .addCase(getTEAMscanActivitySnapshot.pending, (state) => {
        state.getTEAMscanActivitySnapshotStatus = "loading";
      })
      .addCase(
        getTEAMscanActivitySnapshot.fulfilled,
        (state, { payload: { timeFrame, response } }) => {
          return {
            ...state,
            getTEAMscanActivitySnapshotStatus: "succeeded",
            teamScanActivitySnapshotByDateInterval: {
              ...state.teamScanActivitySnapshotByDateInterval,
              [timeFrame]: response,
            },
          };
        }
      )
      .addCase(getTEAMscanActivitySnapshot.rejected, (state) => {
        state.getTEAMscanActivitySnapshotStatus = "failed";
      })
      .addCase(getPeopleWithDeliverabilityIssues.pending, (state) => {
        state.getPeopleWithDeliverabilityIssuesStatus = "loading";
      })
      .addCase(
        getPeopleWithDeliverabilityIssues.fulfilled,
        (state, { payload }) => {
          return {
            ...state,
            getPeopleWithDeliverabilityIssuesStatus: "succeeded",
            peopleWithDeliverabilityIssues: payload,
          };
        }
      )
      .addCase(getPeopleWithDeliverabilityIssues.rejected, (state) => {
        state.getPeopleWithDeliverabilityIssuesStatus = "failed";
      })
      .addCase(getEmailDeliverabilityInformation.pending, (state) => {
        state.getEmailDeliverabilityInformationStatus = "loading";
      })
      .addCase(
        getEmailDeliverabilityInformation.fulfilled,
        (
          state,
          {
            payload,
          }: PayloadAction<{
            response: AllEmailDeliverabilityInformation;
            timeInterval: string;
          }>
        ) => {
          return {
            ...state,
            getEmailDeliverabilityInformationStatus: "succeeded",
            allEmailDeliverabilityInformation: {
              ...state.allEmailDeliverabilityInformation,
              [payload.timeInterval]: payload.response,
            },
          };
        }
      )
      .addCase(getEmailDeliverabilityInformation.rejected, (state) => {
        state.getEmailDeliverabilityInformationStatus = "failed";
      });
  },
});

// ------------------ Selectors -----------------
export const selectDepartmentCardInfoById =
  (departmentId: number | null) => (state: RootState) =>
    departmentId ? state.dashboard.departmentCardInfoById[departmentId] : null;
export const selectGetDepartmentCardInfoStatus = (state: RootState) =>
  state.dashboard.getDepartmentCardInfoStatus;
export const selectGetDepartmentTrackingEventsStatus = (state: RootState) =>
  state.dashboard.getDepartmentTrackingEventsStatus;
export const selectDepartmentTrackingEvents = (state: RootState) =>
  state.dashboard.departmentTrackingEvents;
export const selectTeamHasOpenAssessmentById =
  (teamId: number | null) => (state: RootState) =>
    teamId ? state.dashboard.teamHasOpenAssessmentById[teamId] : null;
export const selectGetTeamHasOpenAssessmentStatus = (state: RootState) =>
  state.dashboard.getTeamHasOpenAssessmentStatus;
export const selectDepartmentTeamLeadersByDepartmentId =
  (departmentId?: number | null) => (state: RootState) =>
    departmentId ? state.dashboard.departmentTeamLeaders[departmentId] : null;
export const selectGetDepartmentAssessmentInfoStatus = (state: RootState) =>
  state.dashboard.getDepartmentAssessmentInfoStatus;
export const selectDepartmentAssessmentInfo = (state: RootState) =>
  state.dashboard.departmentAssessmentInfo;
export const selectGetDepartmentResultsInfoStatus = (state: RootState) =>
  state.dashboard.getDepartmentResultsInfoStatus;
export const selectDepartmentResultsInfoByDepartmentId =
  (departmentId: number | null) => (state: RootState) => {
    return departmentId
      ? state.dashboard.departmentResultsInfoByDepartmentId[departmentId]
      : null;
  };
export const selectRemindTeamLeadersStatus = (state: RootState) =>
  state.dashboard.remindTeamLeadersStatus;
export const selectAllPendingLeadersHaveBeenReminded = (state: RootState) =>
  state.dashboard.allPendingLeadersHaveBeenReminded;
export const selectCheckIfAllPendingLeadersHaveBeenRemindedStatus = (
  state: RootState
) => state.dashboard.checkIfAllPendingLeadersHaveBeenRemindedStatus;
export const selectGetAdminTeamsTeam360AnalyticsTextStatus = (
  state: RootState
) => state.dashboard.getAdminTeamsTeam360AnalyticsTextStatus;
export const selectGetDepartmentPendingTeamLeadersStatus = (state: RootState) =>
  state.dashboard.getDepartmentPendingTeamLeadersStatus;
export const selectAllPendingTeamLeaders = (state: RootState) =>
  state.dashboard.allPendingTeamLeaders;
export const selectGettingAllPendingTeamLeadersStatus = (state: RootState) =>
  state.dashboard.gettingAllPendingTeamLeadersStatus;
export const selectGettingAllPendingDepartmentLeadersStatus = (
  state: RootState
) => state.dashboard.gettingAllPendingDepartmentLeadersStatus;
export const selectAllPendingDepartmentLeaders = (state: RootState) =>
  state.dashboard.allPendingDepartmentLeaders;
export const selectOverallActivitySnapshotByDateInterval = (state: RootState) =>
  state.dashboard.overallActivitySnapshotByDateInterval;
export const selectGetOverallActivitySnapshotStatus = (state: RootState) =>
  state.dashboard.getOverallActivitySnapshotStatus;
export const selectTEAMscanActivitySnapshotByDateInterval = (
  state: RootState
) => state.dashboard.teamScanActivitySnapshotByDateInterval;
export const selectGetTEAMscanActivitySnapshotStatus = (state: RootState) =>
  state.dashboard.getTEAMscanActivitySnapshotStatus;
export const selectHasAnyWeeklyCheckinResults = (state: RootState) =>
  state.dashboard.hasAnyWeeklyCheckinResults;
export const selectSubpageShowing = (state: RootState) =>
  state.dashboard.subpageShowing;
export const selectPeopleWithDeliverabilityIssues = (state: RootState) =>
  state.dashboard.peopleWithDeliverabilityIssues;
export const selectGetPeopleWithDeliverabilityIssuesStatus = (
  state: RootState
) => state.dashboard.getPeopleWithDeliverabilityIssuesStatus;
export const selectAllEmailDeliverabilityInformation = (state: RootState) =>
  state.dashboard.allEmailDeliverabilityInformation;
export const selectGetEmailDeliverabilityInformationStatus = (
  state: RootState
) => state.dashboard.getEmailDeliverabilityInformationStatus;

export const {
  addInvitedLeader,
  remindTeamLeaderForDepartment,
  setSubpageShowing,
} = dashboardSlice.actions;

export default dashboardSlice.reducer;
